import React, { useEffect, useState } from 'react';
import CohortPicker from './CohortPicker';
import ProofModal from './ProofModal';
import { jobsError, useApplications } from '../../hooks/useJobDigest';
import { Badge, Card, count, date, Empty, fullName, INPUT, Pager } from './ui';

const LIMIT = 50;

export default function ApplicationsTab({ cohortIds, setCohortIds }) {
  const [status, setStatus] = useState('pending');
  const [includeTest, setIncludeTest] = useState(false);
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState(null);
  useEffect(() => setOffset(0), [cohortIds, status, includeTest]);

  const { data, isLoading, isFetching, error } = useApplications({
    cohortIds,
    status,
    includeTest,
    limit: LIMIT,
    offset,
  });
  const apps = data?.applications ?? [];

  return (
    <div className="space-y-5">
      <Card>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-start">
          <div className="lg:col-span-2">
            <CohortPicker value={cohortIds} onChange={setCohortIds} />
          </div>
          <div className="space-y-2">
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className={`${INPUT} w-full`}
            >
              <option value="pending">Waiting for review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="all">All</option>
            </select>
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={includeTest}
                onChange={(event) => setIncludeTest(event.target.checked)}
              />
              Include uploads from test emails
            </label>
          </div>
        </div>
      </Card>

      <Card
        title="Proof of applying"
        aside={
          data && (
            <span className="text-xs text-gray-500">
              {count(data.total)} uploads{isFetching ? ' · updating…' : ''}
            </span>
          )
        }
      >
        <p className="mb-4 text-xs text-gray-500">
          Graduates upload a screenshot from the &ldquo;Applied? Upload
          proof&rdquo; link under each job in their email. Only approved proofs
          count as applications in reports.
        </p>
        {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
        {error && (
          <p className="text-sm text-red-700">
            {jobsError(error, "Couldn't load uploads.")}
          </p>
        )}
        {data && apps.length === 0 && (
          <Empty>
            {status === 'pending'
              ? 'Nothing waiting for review.'
              : 'No uploads under these filters.'}
          </Empty>
        )}
        {apps.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-3">Graduate</th>
                  <th className="py-2 pr-3">Job</th>
                  <th className="py-2 pr-3">Cohort</th>
                  <th className="py-2 pr-3">Sent in</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {apps.map((app) => (
                  <tr key={app.id} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3">
                      <div className="font-medium text-gray-900">
                        {fullName(app.student)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {app.student.email}
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      <a
                        href={app.job.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-claret-500 hover:underline"
                      >
                        {app.job.title}
                      </a>
                      <div className="text-xs text-gray-500">
                        {app.job.company}
                      </div>
                      {app.note && (
                        <div className="mt-1 max-w-xs text-xs text-gray-600">
                          &ldquo;{app.note}&rdquo;
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-700">
                      {app.cohort.name || app.cohort.id || '—'}
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-500">
                      {date(app.submitted_at, true)}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge status={app.status} />
                      {app.test && (
                        <span className="ml-1">
                          <Badge status="test" />
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        className="text-xs font-medium text-claret-500 hover:underline"
                        onClick={() => setOpen(app)}
                      >
                        {app.status === 'pending' ? 'Review' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager
          total={data?.total}
          limit={LIMIT}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      <ProofModal
        application={open}
        student={open?.student}
        job={open?.job}
        onClose={() => setOpen(null)}
      />
    </div>
  );
}
